import { Alert, Button, Col, FormGroup, Row } from 'reactstrap';
import Select from 'react-select';
import {
  GoogleMap,
  Marker,
  Polygon,
  useJsApiLoader,
} from '@react-google-maps/api';
import { MAP_STYLE } from '../../constants/MapConstants';
import useAddressLisPicker from '../../hooks/useAddressLisPicker';
import React, { useEffect, useState } from 'react';
import { Coordinate } from '../../models/mapViewModel';
import { POLYGONS_TYPES_OPTIONS } from '../../pages/properties/constants/propertyConstants';
import AssignedAddressCard from './AssignedAddressCard';
import { useToast } from '../toast/ToastProvider';

export interface Props {
  onAddressAssigned: (addressAssignedId: any, centerPoint: any) => void;
  defaultCenter?: Coordinate;
  defaultPolygonId?: number;
  // 'split': listas a la izquierda y mapa a la derecha.
  layout?: 'stacked' | 'split';
  // Asigna la ubicación automáticamente al elegir zona o mover el pin,
  // sin botón intermedio "Asignar ubicación seleccionada".
  autoAssign?: boolean;
}

// El backend manda labels como "Colonia, Residencial": se leen mejor con "o".
const formatLevelLabel = (label: string) => {
  const [first, ...rest] = label.split(',').map((part) => part.trim());
  return rest.length
    ? `${first} o ${rest.map((r) => r.toLowerCase()).join(' o ')}`
    : first;
};

const SectionTitle = ({ title }: { title: string }) => (
  <h5 className="text-uppercase text-muted ls-1 mb-3 mt-4 border-bottom pb-2">
    {title}
  </h5>
);

// `places` lo usa la sección de lugares cercanos de la pantalla de ubicación.
const libraries: any = ['geometry', 'places'];

const AddressListPicker: React.FC<Props> = ({
  onAddressAssigned,
  defaultCenter,
  defaultPolygonId,
  layout = 'stacked',
  autoAssign = false,
}) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: 'AIzaSyB0c1nq-isvWJWWam5CICmPt0oYgY1A_eg',
    libraries: libraries,
  });

  const toast = useToast();
  const [currentAddress, setCurrentAddress] = useState<any>();
  const center = defaultCenter;

  const {
    addressConfiguration,
    addressLevelListsForView,
    setOptionSelected,
    assignablePolygon,
    mapCenter,
    mapZoom,
    setMapCenter,
    onLoadMap,
    pickedAddressApi,
    handlePickAddress,
    existingAssignedPolygon,
  } = useAddressLisPicker({ defaultCenter, defaultPolygonId });

  // Con autoAssign la ubicación se notifica sola cada vez que cambia la zona
  // asignable elegida o la posición del pin.
  const lastSelectedLevel = [...addressLevelListsForView]
    .reverse()
    .find((level) => level.selected);
  useEffect(() => {
    if (!autoAssign || !assignablePolygon || !lastSelectedLevel?.selected) {
      return;
    }
    if (!lastSelectedLevel.level_config?.assignable) return;
    // Ubicación ya guardada y sin tocar: no es un cambio pendiente.
    const isUnchanged =
      String(lastSelectedLevel.selected.value) === String(defaultPolygonId) &&
      mapCenter.lat === center?.lat &&
      mapCenter.lng === center?.lng;
    if (isUnchanged) return;
    const picked = {
      polygon_selected: lastSelectedLevel.selected.value,
      polygon_selected_name: lastSelectedLevel.selected.label,
      center: mapCenter,
    };
    setCurrentAddress(picked);
    onAddressAssigned(picked.polygon_selected, picked.center);
  }, [autoAssign, assignablePolygon, mapCenter, lastSelectedLevel?.selected]);

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading Maps</div>;
  }

  const handleClick = (e: any) => {
    if (window.google && window.google.maps && window.google.maps.geometry) {
      const clickedEventLatLng = e.latLng;
      const { lat, lng } = clickedEventLatLng.toJSON();
      if (assignablePolygon) {
        const googlePolygon = new window.google.maps.Polygon({
          paths: assignablePolygon.polygon,
        });
        const isInside = window.google.maps.geometry.poly.containsLocation(
          clickedEventLatLng,
          googlePolygon,
        );
        if (!isInside) {
          toast.warning(
            `El punto (${lat.toFixed(5)}, ${lng.toFixed(
              5,
            )}) está fuera de la zona seleccionada. Haz clic dentro del área azul.`,
          );
          return;
        }
        setMapCenter({ lat, lng });
      }
    } else {
      console.error('Google Maps Geometry library is not loaded.');
    }
  };

  const listsSection = (
    <>
      <SectionTitle title="Selección de ubicación por listas" />
      {addressLevelListsForView.map((currentAddressLevel, index) => {
        if (!currentAddressLevel.level_config) return null;
        const { admin_label, level, assignable } =
          currentAddressLevel.level_config;
        const optionsToAssign = currentAddressLevel.list;
        const selectedValue = currentAddressLevel.selected;
        const label = formatLevelLabel(admin_label);
        // Cada nivel depende del anterior: sin opciones no se puede elegir.
        const isDisabled = optionsToAssign.length === 0;
        return (
          <FormGroup key={`level-config-fg-${level}`}>
            <label className="form-control-label">
              <span className="badge badge-secondary mr-2">{index + 1}</span>
              {label}
            </label>
            <Select
              key={`level-config-sel-${level}-${index}`}
              placeholder={
                isDisabled && index > 0
                  ? `Primero elige el nivel anterior`
                  : `Seleccione ${label.toLowerCase()}`
              }
              isDisabled={isDisabled}
              value={selectedValue}
              onChange={(item) => {
                if (item) {
                  setOptionSelected({
                    selected_option: item,
                    search_level: level + 1,
                    assignable,
                  });
                }
              }}
              options={optionsToAssign}
            />
            {assignable && selectedValue && (
              <small className="text-info d-block mt-1">
                <i className="fa-solid fa-circle-info mr-1" />
                Esta zona permite asignar propiedades.
              </small>
            )}
          </FormGroup>
        );
      })}
    </>
  );

  const mapSection = (
    <>
      <SectionTitle title="Ubicación en mapa" />
      <GoogleMap
        mapContainerStyle={{
          width: '100%',
          height: layout === 'split' ? '480px' : '400px',
        }}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onLoadMap}
        options={{
          minZoom: 15,
          styles: MAP_STYLE,
        }}
        onClick={(e) => handleClick(e)}
      >
        {assignablePolygon && (
          <Polygon
            key={assignablePolygon.id}
            editable={false}
            draggable={false}
            options={{
              fillColor: '#5e72e4',
              strokeColor: '#5e72e4',
              clickable: true,
            }}
            onClick={(e: any) => {
              if (
                assignablePolygon.polygon_type !== POLYGONS_TYPES_OPTIONS.TOWER
              ) {
                setMapCenter({
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng(),
                });
              }
            }}
            path={assignablePolygon.polygon}
          />
        )}
        <Marker position={mapCenter} />
      </GoogleMap>
      <small className="text-muted d-block mt-2">
        <i className="fa-solid fa-location-dot mr-1" />
        {mapCenter.lat.toFixed(6)}, {mapCenter.lng.toFixed(6)}
        {assignablePolygon
          ? ' · Haz clic dentro de la zona para mover el pin.'
          : ' · Elige una zona asignable para ubicarla en el mapa.'}
      </small>
    </>
  );

  const assignSection = (
    <Row className="mt-2">
      <Col>
        {!autoAssign && assignablePolygon && (
          <Button
            onClick={() => {
              const pickedAddress = handlePickAddress();
              setCurrentAddress(pickedAddress);
              onAddressAssigned(
                pickedAddress.polygon_selected,
                pickedAddress.center,
              );
            }}
            block
            color="success"
            size="md"
            type="button"
          >
            Asignar ubicación seleccionada
          </Button>
        )}
        {currentAddress && (
          <Alert color={autoAssign ? 'info' : 'secondary'} className="mt-2">
            {autoAssign ? (
              <>
                <strong>Cambio pendiente de guardar</strong>
                <br />
                {existingAssignedPolygon?.contained_polygons?.[0]?.name && (
                  <>
                    {existingAssignedPolygon.contained_polygons[0].name}{' '}
                    <i className="fa-solid fa-arrow-right mx-1" />{' '}
                  </>
                )}
                <strong>{currentAddress.polygon_selected_name}</strong>
              </>
            ) : (
              <>
                Asignaras ubicación en:&nbsp;
                <strong>{currentAddress.polygon_selected_name}</strong>
              </>
            )}
            <br />
            <small>
              Coordenada: {currentAddress.center.lat.toFixed(6)},{' '}
              {currentAddress.center.lng.toFixed(6)}
            </small>
          </Alert>
        )}
      </Col>
    </Row>
  );

  if (!addressConfiguration) return <></>;

  return (
    <>
      <AssignedAddressCard
        assigned_polygon={existingAssignedPolygon?.id}
        contained_polygons={existingAssignedPolygon?.contained_polygons}
      />
      {layout === 'split' ? (
        <Row>
          <Col lg={5}>{listsSection}</Col>
          <Col lg={7}>
            {mapSection}
            {assignSection}
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col md={12}>
              {listsSection}
              {mapSection}
            </Col>
          </Row>
          {assignSection}
        </>
      )}
    </>
  );
};

export default AddressListPicker;
